import { useLocation } from "react-router-dom";
import { hasLocalizedPage, localizedPath } from "../content/pages/registry.js";
import {
  DEFAULT_LANGUAGE,
  getExplicitLanguageFromPathname,
} from "../lib/language.js";

/**
 * `?lang=el` on an English URL, moved to the Greek URL once one exists.
 *
 * `app/` and `invite/` already link into this site with `?lang=el`, and those
 * links are in the wild — in emails, in the product, in whatever anyone has
 * shared. They cannot simply stop meaning Greek. But a query parameter is the
 * wrong home for a language: `/wedding-planning?lang=el` and
 * `/wedding-planning` are one URL as far as a crawler is concerned, so the
 * Greek version of a page could never be indexed as itself.
 *
 * So `?lang=` becomes a way IN rather than a way to render: when the requested
 * language has a real page, the visitor is moved to that page's own URL and the
 * parameter is dropped. Every other query parameter is preserved — a campaign
 * link is `?utm_source=…&lang=el`, and losing the attribution to fix the
 * language would be a bad trade.
 *
 * Nothing happens when:
 *
 *   - the path already declares a language — `/el/x?lang=en` keeps rendering
 *     Greek, because the path is the authority and a query parameter must not
 *     be able to override it;
 *   - the requested language has no page yet, which is every page in Phase 1.
 *     The visitor stays where they are and `?lang=` keeps working exactly as it
 *     does today, which is what keeps the existing Greek copy reachable while
 *     the Greek pages are being built.
 *
 * Deliberately a client-side redirect and not a Vercel rule: Vercel evaluates
 * redirects before rewrites, so a rule broad enough to catch `?lang=` on any
 * page would sit in front of every future Greek rewrite.
 */
export const languageQueryRedirect = (
  { pathname, search, hash = "" },
  pages = undefined,
) => {
  // The path wins wherever it says anything at all.
  if (getExplicitLanguageFromPathname(pathname)) return null;

  const params = new URLSearchParams(search);
  const raw = params.get("lang");
  if (!raw) return null;

  // Same mapping as the i18next detector: a prefix match, so `el-GR` counts.
  const lower = raw.toLowerCase();
  const language = lower.startsWith("el")
    ? "el"
    : lower.startsWith("en")
      ? "en"
      : null;

  // An unprefixed URL is already the default language; there is nowhere to go.
  if (!language || language === DEFAULT_LANGUAGE) return null;
  if (!hasLocalizedPage(pathname, language, pages)) return null;

  params.delete("lang");
  const rest = params.toString();
  return `${localizedPath(pathname, language, pages)}${rest ? `?${rest}` : ""}${hash}`;
};

/** The redirect this location calls for, or `null`. */
export default function useLanguageQueryRedirect() {
  const { pathname, search, hash } = useLocation();
  return languageQueryRedirect({ pathname, search, hash });
}
